import React, { useState, useEffect } from 'react';
import * as profileApi from '../../api/profile';

/**
 * The three switches, and the one there is deliberately no switch for.
 *
 * <strong>Saved on the flip rather than behind a Save button.</strong> These are
 * preferences and not a form: there is nothing to validate across them, nothing that can
 * be half-entered, and somebody who has come here to stop the pick reminders should not
 * have to find a second button to make it true.
 *
 * <strong>Password reset is named on the screen even though it has no toggle.</strong>
 * Leaving it off the list would suggest turning everything off turns off everything, and
 * the first person to unsubscribe and then need their password back would find out
 * otherwise at the worst moment. It cannot be switched off - the server refuses, and the
 * rule lives on the template rather than in a settings table for exactly that reason - so
 * the honest thing is to say so here.
 */
const SWITCHES = [
  {
    key: 'notifyInvitations',
    label: 'Invitations',
    hint: 'When somebody invites you to a game or a league.',
  },
  {
    key: 'notifyRequests',
    label: 'Join requests',
    hint: 'When somebody asks to join a league you run. One on the first, then a daily summary.',
  },
  {
    key: 'notifyPicks',
    label: 'Pick reminders',
    hint: 'When a week is about to close and you have not picked. Miss it and you are out.',
  },
  {
    key: 'notifyResults',
    label: 'Season results',
    // Deliberately not "who won" - the email does not say, and a hint promising a name would be
    // a promise the message does not keep.
    hint: 'When a league you are in finishes for the season.',
  },
];

function EmailPreferencesField() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    profileApi.getEmailPreferences()
      .then((result) => { if (live) setPrefs(result); })
      .catch(() => { if (live) setError('Could not load your email settings'); });
    return () => { live = false; };
  }, []);

  const toggle = async (key) => {
    if (!prefs) return;
    // Kept by name so the failure branch reads as what it is - putting the switch back.
    const previous = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    // Shown immediately, then corrected if the server disagrees. A switch that waits for
    // a round trip before moving feels broken on a phone.
    setPrefs(next);
    setError('');
    setBusy(true);
    try {
      setPrefs(await profileApi.updateEmailPreferences(next));
    } catch (err) {
      setPrefs(previous);
      setError(err.name === 'ApiError'
        ? (err.body?.error || 'Could not save that')
        : 'Could not save that');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-field email-prefs-field">
      <span className="field-label">Email:</span>

      {error && <span className="error-text">{error}</span>}

      {prefs === null && !error && <span className="helper-text">Loading…</span>}

      {prefs !== null && SWITCHES.map((option) => (
        <label key={option.key} className="email-pref-row">
          <input
            type="checkbox"
            checked={Boolean(prefs[option.key])}
            disabled={busy}
            onChange={() => toggle(option.key)}
          />
          <span className="email-pref-text">
            <span className="email-pref-label">{option.label}</span>
            <span className="helper-text">{option.hint}</span>
          </span>
        </label>
      ))}

      <span className="helper-text">
        Password resets and setup links always come through, whatever is set here — an
        account nobody can get back into is worse than an email nobody wanted.
      </span>
    </div>
  );
}

export default EmailPreferencesField;
